if (!customElements.get("color-swatch")) {
  customElements.define(
    "color-swatch",
    class ColorSwatch extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.initialize();
      }

      initialize() {
        this.initProperties();
        this.sortButtons();       // per container
        this.initCounters();      // per container
        this.initButtons();       // wire events & preselects
      }

      /* ---------- Setup ---------- */
      initProperties() {
        const node = this;

        this.container = node.querySelector(".card__container");
        this.adnotation = node.querySelector(".card");
        [this.img, this.hover_img] = this.container?.querySelectorAll(".card__img") || [];
        this.video = this.querySelector("video");
        this.loader = this.querySelector(".card__loader");

        // There can be two containers (mobile_only + desktop_only)
        this.color_swatcher_containers = Array.from(
          this.container?.querySelectorAll(".card__color-swatcher--container") || []
        );

        // All wrappers across both containers (used for syncing active state)
        this.all_wrappers = Array.from(
          this.container?.querySelectorAll(".color-swatcher--wrapper") || []
        );

        // Store last “clicked” (committed) state so mouseleave can restore it
        this.clicked_href = this.adnotation?.getAttribute("href") || "";
        this.clicked_img = this.img?.getAttribute("src") || "";
        this.clicked_srcset = this.img?.getAttribute("srcset") || "";
        this.clicked_hover_img = this.hover_img?.getAttribute("src") || "";

        // activeFilters / activeOptions come from the page (if present)
        this.activeFilters = null;
        try { this.activeFilters = activeFilters; } catch (e) {}

        this.activeOptions = {};
        try { this.activeOptions = activeOptions || {}; } catch (e) {}

        // Read optionsAsColorSwatches from the first container that has it
        const firstWithDataset = this.color_swatcher_containers.find(c => c?.dataset?.optionsAsColorSwatches);
        this.optionsAsColorSwatches = firstWithDataset?.dataset?.optionsAsColorSwatches?.split(",") || [];
        
        this.isCheckedOption = 0;
        this.isCheckedActiveFilter = false;
        this.isVideoHidden = false;
      }

      /* ---------- Sorting (per container) ---------- */
      sortButtons() {
        this.color_swatcher_containers.forEach(container => {
          const wrappers = Array.from(container.querySelectorAll(".color-swatcher--wrapper"));
          if (!wrappers.length) return;

          wrappers
            .sort((a, b) => {
              const aUn = a.classList?.contains("unavailable");
              const bUn = b.classList?.contains("unavailable");
              return aUn && !bUn ? 1 : (!aUn && bUn ? -1 : 0);
            })
            .forEach(w => container.append(w));
        });

        // Refresh global list after re-append
        this.all_wrappers = Array.from(
          this.container?.querySelectorAll(".color-swatcher--wrapper") || []
        );
      }

      /* ---------- Counters & initial clamp (per container) ---------- */
      initCounters() {
        this.color_swatcher_containers.forEach(container => {
          // ensure a counter element exists
          let counter = container.querySelector(".color-swatcher--counter");
          if (!counter) {
            counter = document.createElement("span");
            counter.classList.add("color-swatcher--counter");
            container.appendChild(counter);
          }

          const wrappers = Array.from(container.querySelectorAll(".color-swatcher--wrapper"));
          // hide > 4 per container
          wrappers.forEach((w, idx) => {
            if (idx > 3) w.classList.add("hidden");
          });

          if (wrappers.length > 4) {
            counter.textContent = `+ ${wrappers.length - 4}`;
            container.addEventListener("mouseover", () => this.showAllInContainer(container));
          } else {
            counter.textContent = "";
            counter.classList.add("hidden");
          }
        });
      }

      showAllInContainer(container) {
        const wrappers = Array.from(container.querySelectorAll(".color-swatcher--wrapper"));
        wrappers.forEach(w => w.classList.remove("hidden"));
        const counter = container.querySelector(".color-swatcher--counter");
        counter?.classList.add("hidden");
      }

      /* ---------- Button wiring ---------- */
      initButtons() {
        if (!this.all_wrappers.length) return;

        // Attach container-level restore on mouseleave for BOTH containers
        this.color_swatcher_containers.forEach(container => {
          container.addEventListener("mouseleave", () => this.restoreAttributes());
        });

        // Wire every wrapper (across both mobile/desktop)
        this.all_wrappers.forEach((wrapper, index) => {
          const button = wrapper.querySelector(".color-swatcher");
          if (!button) return;

          const attributes = this.getButtonAttributes(button);
          const tooltip = wrapper.querySelector(".color-swatcher--tooltip");

          // Preselect by activeOptions once (first match)
          if (this.isCheckedOption === 0) {
            this.checkFilterOption(wrapper, attributes);
          }

          // Preselect by activeFilters (URL/panel filters)
          if (this.activeFilters && !this.isCheckedActiveFilter) {
            const colorName = attributes.dataColor;
            for (const option of this.optionsAsColorSwatches) {
              const options = this.activeFilters[option];
              if (options && options[colorName]) {
                this.handleClickEvent(wrapper, attributes, /*syncOnly=*/false);
                this.isCheckedActiveFilter = true;
                break;
              }
            }
          }

          // Hide items >4 INSIDE THEIR container (already done in initCounters)
          // Assign events
          wrapper.addEventListener("click", () => this.handleClickEvent(wrapper, attributes));
          wrapper.addEventListener("keydown", (e) => { if (e.key === "Enter") this.handleClickEvent(wrapper, attributes); });
          wrapper.addEventListener("mouseover", () => this.handleMouseOverEvent(wrapper, attributes, tooltip));
          wrapper.addEventListener("mouseout", () => this.handleMouseOutEvent(tooltip));
        });

        // If nothing was selected by filters, you can optionally auto-select the first:
        // this.setFirstOptionActive();
      }

      getButtonAttributes(button) {
        return {
          dataColor: button.getAttribute("data-color"),
          button_href: button.getAttribute("data-href"),
          button_img: button.getAttribute("data-img"),
          button_srcset: button.getAttribute("data-srcset"),
          button_hover: button.getAttribute("data-hover"),
        };
      }

      checkFilterOption(wrapper, { dataColor, button_href, button_img, button_hover, button_srcset }) {
        const entries = Object.entries(this.activeOptions || {});
        for (const [, arr] of entries) {
          if (Array.isArray(arr) && arr.indexOf(dataColor) !== -1) {
            this.commitVisuals(button_href, button_img, button_srcset, button_hover);
            this.syncActiveByColor(dataColor);
            this.isCheckedOption = 1;
            break;
          }
        }
      }

      /* ---------- Events ---------- */
      handleClickEvent(wrapper, { dataColor, button_href, button_img, button_hover, button_srcset }, syncOnly = false) {
        if (!button_img) return;

        this.showLoader();
        this.commitVisuals(button_href, button_img, button_srcset, button_hover);
        this.syncActiveByColor(dataColor);

        // Commit restore state
        this.clicked_href = button_href;
        this.clicked_img = button_img;
        this.clicked_srcset = button_srcset;
        this.clicked_hover_img = button_hover;

        // Show image (hide video)
        this.video?.classList.add("hidden");
        this.isVideoHidden = true;
        this.img?.classList.remove("hidden");
        if (this.img) {
          this.img.onload = () => {
            this.hideLoader();
            this.img.onload = null;
          };
        } else {
          this.hideLoader();
        }
      }

      handleMouseOverEvent(wrapper, { button_href, button_img, button_hover, button_srcset }, tooltip) {
        this.setAttributes(this.adnotation, { href: button_href });
        this.setAttributes(this.img, { src: button_img });
        this.setAttributes(this.img, { srcset: button_srcset });
        this.checkHoverImage(button_hover);

        if (tooltip) {
          tooltip.classList.remove("hidden");
          this.setTooltipPosition(tooltip);
        }
      }

      handleMouseOutEvent(tooltip) {
        if (!tooltip) return;
        tooltip.classList.add("hidden");
        tooltip.classList.remove("color-swatcher--tooltip-left", "color-swatcher--tooltip-right");
      }

      /* ---------- Helpers ---------- */
      commitVisuals(href, img, srcset, hover) {
        this.setAttributes(this.adnotation, { href });
        this.setAttributes(this.img, { src: img });
        this.setAttributes(this.img, { srcset: srcset || "" });
        this.checkHoverImage(hover);
      }

      syncActiveByColor(colorName) {
        // Remove active everywhere, then activate wrappers that match color across BOTH containers
        this.all_wrappers.forEach(w => w.classList.remove("active"));
        this.all_wrappers.forEach(w => {
          const btn = w.querySelector(".color-swatcher");
          if (btn?.getAttribute("data-color") === colorName) {
            w.classList.add("active");
          }
        });
      }

      restoreAttributes() {
        this.setAttributes(this.adnotation, { href: this.clicked_href });
        this.setAttributes(this.img, { src: this.clicked_img });
        this.setAttributes(this.img, { srcset: this.clicked_srcset || "" });
        this.checkHoverImage(this.clicked_hover_img);
      }

      setFirstOptionActive() {
        const first = this.all_wrappers[0];
        if (!first) return;
        const btn = first.querySelector(".color-swatcher");
        if (!btn) return;
        const attrs = this.getButtonAttributes(btn);
        this.commitVisuals(attrs.button_href, attrs.button_img, attrs.button_srcset, attrs.button_hover);
        this.syncActiveByColor(attrs.dataColor);
      }

      setTooltipPosition(tooltip) {
        const rect = tooltip.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        if (!rect) return;
        if (rect.x < 0) {
          tooltip.classList.add("color-swatcher--tooltip-left");
        } else if (rect.right > screenWidth) {
          tooltip.classList.add("color-swatcher--tooltip-right");
        }
      }

      showLoader() {
        if (!this.loader) return;
        this.loader.classList.remove("hidden");
        this.loader.innerHTML = '<div class="spinner-ring"></div>';
        if (this.img) this.img.style.opacity = 0;
      }

      hideLoader() {
        if (!this.loader) return;
        this.loader.classList.add("hidden");
        this.loader.innerHTML = "";
        if (this.img) this.img.style.opacity = 1;
      }

      setAttributes(el, attrs) {
        if (!el || !attrs) return;
        for (const k in attrs) el.setAttribute(k, attrs[k]);
      }

      checkHoverImage(button_hover) {
        if (button_hover && this.hover_img) {
          this.setAttributes(this.hover_img, { src: button_hover });
        }
      }
    }
  );
}